/**
 * Citation and reference formatting utilities
 * Supports multiple citation styles: BibTeX, APA, IEEE, Chicago, Harvard, MLA, MHRA, Vancouver, Markdown, Plain Text, Word XML
 */

/**
 * Format references as BibTeX
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted BibTeX string
 */
export const formatBibtex = (refs) => {
  return refs.map(r => r.bibtex_raw).join('\n\n');
};

/**
 * Format references as plain numbered list
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted plain text string
 */
export const formatPlainText = (refs) => {
  return refs.map((r, i) => {
    let text = `${i + 1}. ${r.author || 'Unknown'} (${r.year || 'n.d.'}). ${r.title}.`;
    if (r.journal) text += ` ${r.journal}.`;
    if (r.doi) text += ` DOI: ${r.doi}`;
    return text;
  }).join('\n\n');
};

/**
 * Format references in APA (American Psychological Association) style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted APA citations
 */
export const formatAPA = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const year = r.year || 'n.d.';
    const title = r.title;
    let citation = `${authors} (${year}). ${title}.`;
    if (r.journal) {
      citation += ` ${r.journal}`;
      if (r.volume) citation += `, ${r.volume}`;
      if (r.pages) citation += `, ${r.pages}`;
      citation += '.';
    }
    if (r.doi) citation += ` https://doi.org/${r.doi}`;
    return `${i + 1}. ${citation}`;
  }).join('\n\n');
};

/**
 * Format references in IEEE style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted IEEE citations
 */
export const formatIEEE = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const title = r.title;
    const year = r.year || 'n.d.';
    let citation = `[${i + 1}] ${authors}, "${title},"`;
    if (r.journal) {
      citation += ` ${r.journal}`;
      if (r.volume) citation += `, vol. ${r.volume}`;
      if (r.number) citation += `, no. ${r.number}`;
      if (r.pages) citation += `, pp. ${r.pages}`;
      citation += `, ${year}.`;
    } else if (r.booktitle) {
      citation += ` in ${r.booktitle}`;
      if (r.pages) citation += `, pp. ${r.pages}`;
      citation += `, ${year}.`;
    } else {
      citation += ` ${year}.`;
    }
    if (r.doi) citation += ` doi: ${r.doi}`;
    return citation;
  }).join('\n\n');
};

/**
 * Format references in Chicago style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted Chicago citations
 */
export const formatChicago = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const year = r.year || 'n.d.';
    const title = r.title;
    let citation = `${authors}. ${year}. "${title}."`;
    if (r.journal) {
      citation += ` ${r.journal}`;
      if (r.volume) citation += ` ${r.volume}`;
      if (r.number) citation += ` (${r.number})`;
      if (r.pages) citation += `: ${r.pages}`;
      citation += '.';
    } else if (r.booktitle) {
      citation += ` In ${r.booktitle}`;
      if (r.pages) citation += `, ${r.pages}`;
      citation += '.';
    }
    if (r.doi) citation += ` https://doi.org/${r.doi}.`;
    return `${i + 1}. ${citation}`;
  }).join('\n\n');
};

/**
 * Format references in Harvard style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted Harvard citations
 */
export const formatHarvard = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const year = r.year || 'n.d.';
    const title = r.title;
    let citation = `${authors} (${year}) '${title}'`;
    if (r.journal) {
      citation += `, ${r.journal}`;
      if (r.volume) citation += `, ${r.volume}`;
      if (r.number) citation += `(${r.number})`;
      if (r.pages) citation += `, pp. ${r.pages}`;
      citation += '.';
    } else if (r.booktitle) {
      citation += `, in ${r.booktitle}`;
      if (r.pages) citation += `, pp. ${r.pages}`;
      citation += '.';
    }
    if (r.doi) citation += ` doi: ${r.doi}.`;
    return `${i + 1}. ${citation}`;
  }).join('\n\n');
};

/**
 * Format references in MLA (Modern Language Association) style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted MLA citations
 */
export const formatMLA = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const title = r.title;
    let citation = `${authors}. "${title}."`;
    if (r.journal) {
      citation += ` ${r.journal}`;
      if (r.volume) citation += `, vol. ${r.volume}`;
      if (r.number) citation += `, no. ${r.number}`;
      if (r.year) citation += `, ${r.year}`;
      if (r.pages) citation += `, pp. ${r.pages}`;
      citation += '.';
    } else if (r.booktitle) {
      citation += ` ${r.booktitle}`;
      if (r.publisher) citation += `, ${r.publisher}`;
      if (r.year) citation += `, ${r.year}`;
      if (r.pages) citation += `, pp. ${r.pages}`;
      citation += '.';
    }
    if (r.doi) citation += ` doi:${r.doi}.`;
    return `${i + 1}. ${citation}`;
  }).join('\n\n');
};

/**
 * Format references in MHRA (Modern Humanities Research Association) style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted MHRA citations
 */
export const formatMHRA = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const title = r.title;
    let citation = `${authors}, '${title}'`;
    if (r.journal) {
      citation += `, ${r.journal}`;
      if (r.volume) citation += `, ${r.volume}`;
      if (r.number) citation += `.${r.number}`;
      if (r.year) citation += ` (${r.year})`;
      if (r.pages) citation += `, ${r.pages}`;
    } else if (r.booktitle) {
      citation += `, in ${r.booktitle}`;
      if (r.publisher) citation += ` (${r.publisher}`;
      if (r.year) citation += `, ${r.year}`;
      if (r.publisher) citation += ')';
      if (r.pages) citation += `, pp. ${r.pages}`;
    }
    if (r.doi) citation += ` <https://doi.org/${r.doi}>`;
    return `${i + 1}. ${citation}`;
  }).join('\n\n');
};

/**
 * Format references in Vancouver style
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted Vancouver citations
 */
export const formatVancouver = (refs) => {
  return refs.map((r, i) => {
    const authors = r.author || 'Unknown';
    const title = r.title;
    const year = r.year || 'n.d.';
    let citation = `${i + 1}. ${authors}. ${title}.`;
    if (r.journal) {
      citation += ` ${r.journal}.`;
      if (r.year) citation += ` ${r.year}`;
      if (r.volume) citation += `;${r.volume}`;
      if (r.number) citation += `(${r.number})`;
      if (r.pages) citation += `:${r.pages}`;
      citation += '.';
    } else if (r.booktitle) {
      citation += ` In: ${r.booktitle}.`;
      if (r.publisher) citation += ` ${r.publisher}`;
      citation += `; ${year}.`;
      if (r.pages) citation += ` p. ${r.pages}.`;
    }
    if (r.doi) citation += ` doi: ${r.doi}`;
    return citation;
  }).join('\n\n');
};

/**
 * Format references as Markdown
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted Markdown string
 */
export const formatMarkdown = (refs) => {
  return refs.map((r, i) => {
    let md = `${i + 1}. **${r.title}**\n`;
    md += `   - Authors: ${r.author || 'Unknown'}\n`;
    md += `   - Year: ${r.year || 'n.d.'}\n`;
    if (r.journal) md += `   - Journal: ${r.journal}\n`;
    if (r.doi) md += `   - DOI: [${r.doi}](https://doi.org/${r.doi})\n`;
    return md;
  }).join('\n');
};

/**
 * Escape XML special characters
 * @param {string} str - String to escape
 * @returns {string} Escaped XML string
 */
export const escapeXml = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

/**
 * Parse author names for Word XML format
 * @param {string} authorString - Author string from BibTeX
 * @returns {Array} Array of author objects with first and last names
 */
export const parseAuthors = (authorString) => {
  if (!authorString) return [];
  // Split by 'and' or comma
  const authors = authorString.split(/\s+and\s+|,\s*/);
  return authors.map(author => {
    const trimmed = author.trim();
    // Try to split into last name and first name
    const parts = trimmed.split(/\s+/);
    if (parts.length >= 2) {
      const last = parts[parts.length - 1];
      const first = parts.slice(0, -1).join(' ');
      return { first, last };
    }
    return { first: '', last: trimmed };
  });
};

/**
 * Map BibTeX entry types to Word source types
 * @param {string} entryType - BibTeX entry type
 * @returns {string} Word XML source type
 */
export const mapSourceType = (entryType) => {
  const typeMap = {
    'article': 'JournalArticle',
    'book': 'Book',
    'inproceedings': 'ConferenceProceedings',
    'conference': 'ConferenceProceedings',
    'incollection': 'BookSection',
    'inbook': 'BookSection',
    'proceedings': 'ConferenceProceedings',
    'phdthesis': 'Report',
    'mastersthesis': 'Report',
    'techreport': 'Report',
    'manual': 'Report',
    'unpublished': 'Misc',
    'misc': 'Misc',
    'online': 'ElectronicSource',
    'electronic': 'ElectronicSource'
  };
  return typeMap[entryType?.toLowerCase()] || 'Misc';
};

/**
 * Generate a GUID for Word XML
 * @returns {string} Generated GUID
 */
export const generateGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Format references as Microsoft Word XML
 * @param {Array} refs - Array of reference objects
 * @returns {string} Formatted Word XML string
 */
export const formatWordXML = (refs) => {
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<b:Sources xmlns:b="http://schemas.openxmlformats.org/officeDocument/2006/bibliography" ';
  xml += 'xmlns="http://schemas.openxmlformats.org/officeDocument/2006/bibliography" ';
  xml += 'SelectedStyle="\\APA.XSL" StyleName="APA">\n';

  refs.forEach((ref, index) => {
    const authors = parseAuthors(ref.author);
    const sourceType = mapSourceType(ref.entry_type);
    const tag = ref.id || `Ref${index + 1}`;

    xml += '  <b:Source>\n';
    xml += `    <b:Tag>${escapeXml(tag)}</b:Tag>\n`;
    xml += `    <b:SourceType>${sourceType}</b:SourceType>\n`;
    xml += `    <b:Guid>{${generateGuid()}}</b:Guid>\n`;

    // Title
    if (ref.title) {
      xml += `    <b:Title>${escapeXml(ref.title)}</b:Title>\n`;
    }

    // Year
    if (ref.year) {
      xml += `    <b:Year>${escapeXml(ref.year)}</b:Year>\n`;
    }

    // Authors
    if (authors.length > 0) {
      xml += '    <b:Author>\n';
      xml += '      <b:Author>\n';
      xml += '        <b:NameList>\n';
      authors.forEach(author => {
        xml += '          <b:Person>\n';
        if (author.last) {
          xml += `            <b:Last>${escapeXml(author.last)}</b:Last>\n`;
        }
        if (author.first) {
          xml += `            <b:First>${escapeXml(author.first)}</b:First>\n`;
        }
        xml += '          </b:Person>\n';
      });
      xml += '        </b:NameList>\n';
      xml += '      </b:Author>\n';
      xml += '    </b:Author>\n';
    }

    // Journal name
    if (ref.journal) {
      xml += `    <b:JournalName>${escapeXml(ref.journal)}</b:JournalName>\n`;
    }

    // Book title (for conference proceedings, book sections, etc.)
    if (ref.booktitle) {
      xml += `    <b:ConferenceName>${escapeXml(ref.booktitle)}</b:ConferenceName>\n`;
    }

    // Volume
    if (ref.volume) {
      xml += `    <b:Volume>${escapeXml(ref.volume)}</b:Volume>\n`;
    }

    // Issue/Number
    if (ref.number) {
      xml += `    <b:Issue>${escapeXml(ref.number)}</b:Issue>\n`;
    }

    // Pages
    if (ref.pages) {
      xml += `    <b:Pages>${escapeXml(ref.pages)}</b:Pages>\n`;
    }

    // Publisher
    if (ref.publisher) {
      xml += `    <b:Publisher>${escapeXml(ref.publisher)}</b:Publisher>\n`;
    }

    // DOI
    if (ref.doi) {
      xml += `    <b:DOI>${escapeXml(ref.doi)}</b:DOI>\n`;
    }

    // URL
    if (ref.url) {
      xml += `    <b:URL>${escapeXml(ref.url)}</b:URL>\n`;
    }

    // Abstract
    if (ref.abstract) {
      xml += `    <b:Comments>${escapeXml(ref.abstract)}</b:Comments>\n`;
    }

    xml += '  </b:Source>\n';
  });

  xml += '</b:Sources>';
  return xml;
};
